import { Button, ButtonProps, createStyles, makeStyles, Theme, Tooltip, TooltipProps } from '@material-ui/core';
import { SvgIconComponent } from '@material-ui/icons';
import classNames from 'classnames';
import React, { useState } from 'react';

import { spacings, textSizes, TextSizesEnum } from '../../../theme';
import { ButtonColor, buttonStyleColors } from './PlexButton';

export interface Props {
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  MuiIcon?: SvgIconComponent;
  SvgIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  iconView?: React.ReactNode;
  color?: ButtonColor;
  tooltip?: string;
  tooltipPlacement?: TooltipProps['placement'];
  size?: IconButtonSize;
  className?: string;
  muiButtonClasses?: ButtonProps['classes'];
  disabled?: boolean;
  isOverButton?: boolean;
}

export enum IconButtonSize {
  Small = 18,
  Normal = 24,
  Large = 34,
}

export const PlexIconButton = React.memo((props: Props) => {
  const {
    onClick,
    disabled,
    MuiIcon,
    SvgIcon,
    iconView,
    tooltip,
    tooltipPlacement,
    className,
    isOverButton,
    muiButtonClasses,
  } = props;
  const classes = useStyles(props);
  // Unfortunately we are controlling the tooltip state manually because of this issue
  // which seems to be marked as closed but is still happening
  // https://github.com/mui-org/material-ui/issues/12347
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const onMouseEnter = (): void => {
    setTooltipOpen(true);
  };
  const onMouseLeave = (): void => {
    setTooltipOpen(false);
  };
  const onClickWrapped = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    if (isOverButton) event.stopPropagation();
    if (onClick) onClick(event);
    setTooltipOpen(false);
  };
  const buttonElement = ({ isRoot }: { isRoot: boolean }): React.ReactElement => (
    <Button
      onClick={onClickWrapped}
      color={'primary'}
      variant={'contained'}
      classes={{
        ...muiButtonClasses,
        root: classNames(muiButtonClasses?.root, classes.muiButtonRoot, isRoot && className),
        containedPrimary: classNames(muiButtonClasses?.containedPrimary, classes.muiButtonContainedPrimary),
        label: classNames(muiButtonClasses?.label, classes.muiButtonLabel),
      }}
      disabled={disabled}
      onMouseDown={(event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        if (isOverButton) {
          event.stopPropagation();
        }
      }}
    >
      {MuiIcon && <MuiIcon classes={{ root: classes.muiSvgIconRoot }} />}
      {SvgIcon && <SvgIcon className={classes.svgIconRoot} />}
      {iconView}
    </Button>
  );
  if (!!tooltip) {
    return (
      <Tooltip
        open={tooltipOpen}
        title={tooltip}
        placement={tooltipPlacement}
        disableFocusListener
        disableHoverListener
      >
        <span
          /* use wrapper since mouse enter / leave are not triggered if the button is disabled */
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={classNames(className, classes.wrapperSpan)}
        >
          {buttonElement({ isRoot: false })}
        </span>
      </Tooltip>
    );
  }

  return buttonElement({ isRoot: true });
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    muiButtonRoot: ({ color = ButtonColor.Primary }: Props) => ({
      display: 'flex',
      backgroundColor: buttonStyleColors[color].background,
      padding: spacings.small,
      minWidth: 10,
    }),
    muiButtonContainedPrimary: ({ color = ButtonColor.Primary }: Props) => {
      if (color === ButtonColor.Transparent) {
        return {
          '&:hover': {
            backgroundColor: buttonStyleColors[ButtonColor.TransparentGray].hover,
          },
          '&:active': {
            backgroundColor: buttonStyleColors[ButtonColor.TransparentGray].hover,
          },
        };
      } else {
        return {
          '&:hover': {
            backgroundColor: buttonStyleColors[color].hover,
          },
          '&:active': {
            backgroundColor: buttonStyleColors[color].hover,
          },
        };
      }
    },
    muiButtonLabel: ({ color = ButtonColor.Primary }: Props) => ({
      color: buttonStyleColors[color].label,
      fontSize: textSizes[TextSizesEnum.Normal].fontSize,
      lineHeight: textSizes[TextSizesEnum.Normal].lineHeight,
    }),
    muiSvgIconRoot: ({ size = IconButtonSize.Small }: Props) => ({
      display: 'flex',
      width: size,
      height: size,
      minWidth: size,
      minHeight: size,
    }),
    svgIconRoot: ({ size = IconButtonSize.Small, color = ButtonColor.Primary }: Props) => {
      if (color === ButtonColor.Transparent) {
        return {
          display: 'flex',
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
        };
      } else {
        return {
          display: 'flex',
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          fill: `${buttonStyleColors[color].label} !important`, // sets the svg color
        };
      }
    },
    wrapperSpan: {
      display: 'flex',
    },
  })
);
